import { ChangeDetectionStrategy, Component, Inject, Injector, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AppTextService } from '@fi-shared/app-text';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { DocumentModel } from '../../document.model';
import { FiCommentSettingType, FiPublicationActionType } from '@fi-shared/types';
import { FI_DIALOGS_CONTENT_DATA, FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogsContainerBase } from '@fi-shared/fi-dialogs/fi-dialogs-container-base';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';

export interface IDocumentDialogEditConfirmData {
    document: DocumentModel;
    documentName: string;
    openSettings: () => void;
    openSharing: () => void;
    sharingText: string;
}

@Component({
    selector: 'fi-document-confirm-save',
    templateUrl: './document-confirm-save.component.html',
    styleUrls: ['./document-confirm-save.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-document-confirm-save' },
    standalone: false,
})
export class DocumentDialogEditConfirmSaveComponent extends FiDialogsContentBase<IDocumentDialogEditConfirmData, void> {
    constructor(
        dialogRef: MatDialogRef<FiDialogsContainerBase<IDocumentDialogEditConfirmData, void>>,
        @Inject(FI_DIALOGS_CONTENT_DATA) data: IDocumentDialogEditConfirmData,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        injector: Injector,
    ) {
        super(dialogRef, data, injector);
    }

    openSettings() {
        this.dialogRef.close();
        this.data.openSettings();
    }

    openSharing() {
        this.dialogRef.close();
        this.data.openSharing();
    }

    getShowAsNewsText() {
        return this.appTextService.get(
            this.data.document.DocumentSetting.IsNews
                ? 'document.show_as_news_text'
                : 'document.save_confirm.dont_show_as_news',
        );
    }

    getSendPushMessagesText() {
        return this.appTextService.get(
            this.data.document.DocumentSetting.SendPushMessages
                ? 'common.send_push_messages'
                : 'common.not_send_push_messages',
        );
    }

    getCommentsText() {
        return this.appTextService.get(
            this.data.document.DocumentSetting.CommentSettingType === FiCommentSettingType.AllowAllComments
                ? 'common.allow_comments'
                : 'common.no_comments',
        );
    }

    getSharingDatesText() {
        if (!this.data.document.PublicationSetting.IsShared)
            return this.appTextService.get('document.save_confirm.not_shared');

        if (this.data.document.PublicationSetting.PublicationType === PublicationType.Continous)
            return this.appTextService.get('document.save_confirm.shared_continuous', {
                '{start_date}': this.fiFormatDateService.formatDate(
                    this.data.document.PublicationSetting.PublicationStartDate,
                    'date',
                ),
            });

        if (this.data.document.PublicationSetting.PublicationType === PublicationType.TimeLimited)
            return this.appTextService.get('document.save_confirm.shared_time_limited', {
                '{start_date}': this.fiFormatDateService.formatDate(
                    this.data.document.PublicationSetting.PublicationStartDate,
                    'date',
                ),
                '{end_date}': this.fiFormatDateService.formatDate(
                    this.data.document.PublicationSetting.PublicationEndDate,
                    'date',
                ),
            });
    }

    isTimeLimitedSharing() {
        return (
            this.data.document.PublicationSetting.IsShared &&
            this.data.document.PublicationSetting.PublicationType === PublicationType.TimeLimited
        );
    }

    getTimeLimitedText() {
        switch (this.data.document.PublicationSetting.PublicationActionType) {
            case FiPublicationActionType.DeleteDocument:
                return this.appTextService.get('document_save_confirm.delete_after_end_date');
            case FiPublicationActionType.UnpublishDocument:
                return this.appTextService.get('document_save_confirm.unpublish_after_end_date');
        }
    }

    isShared() {
        return this.data.document.PublicationSetting.IsShared;
    }

    getSafeHtml(str: string) {
        return fiTextToHtml(str);
    }
}
